// utils/transportEligibility.js

function hasAirportStop(g, stopType, airportCode, airportNameByCode) {
  return g.stops.some(
    (s) =>
      String(s?.stopType || "").toUpperCase() === String(stopType).toUpperCase() &&
      stopMatchesAirport(s, airportCode, airportNameByCode)
  );
}

function hasCampusStop(g, stopType, campusName, campusByName) {
  return g.stops.some(
    (s) =>
      String(s?.stopType || "").toUpperCase() === String(stopType).toUpperCase() &&
      stopMatchesCampus(s, campusName, campusByName)
  );
}

export function makeEligibilityContext({ airports = [], campuses = [] }) {
  const airportNameByCode = new Map();
  airports.forEach((a) => airportNameByCode.set(String(a.code).toUpperCase(), a.name));

  const campusByName = new Map();
  campuses.forEach((c) => {
    const name = (c.campusName || c.name || "").trim();
    if (name) campusByName.set(name.toLowerCase(), c);
  });

  return { airportNameByCode, campusByName };
}

function stopMatchesAirport(stop, airportCode, airportNameByCode) {
  if (!airportCode) return false;
  const code = String(airportCode).toUpperCase();
  const airportName = airportNameByCode.get(code);

  const loc = String(stop?.location || "").toLowerCase();
  if (loc.includes(code.toLowerCase())) return true;
  if (airportName && loc.includes(String(airportName).toLowerCase())) return true;

  return false;
}

function stopMatchesCampus(stop, campusName, campusByName) {
  if (!campusName) return false;
  const cn = String(campusName).toLowerCase();
  const campus = campusByName.get(cn);

  if (campus?.id && stop?.campusId && Number(stop.campusId) === Number(campus.id)) return true;

  const loc = String(stop?.location || "").toLowerCase();
  return loc.includes(cn);
}

export function busEligibilityForRow(t, g, ctx) {
  const { airportNameByCode, campusByName } = ctx;

  if (!g?.stops || g.stops.length === 0) return { ok: false, reason: "Bus has no stops" };

  const dir = String(t?.direction || "").toUpperCase();
  
  const isSelf = t?.privatePickup === true;
  const hasTransfer = t?.transferId && Number(t.transferId) !== 0;

  if (dir !== "INTERNAL" && !isSelf && !hasTransfer) {
    return { ok: false, reason: "Missing transfer" };
  }

  if (dir === "IN") {
    const requiredAirport = t?.destination;
    const requiredCampus = t?.campus;
    if (!requiredAirport || !requiredCampus) return { ok: false, reason: "Missing airport/campus" };

    const airportPickup = hasAirportStop(g, "PICKUP", requiredAirport, airportNameByCode);
    if (!airportPickup) return { ok: false, reason: `No PICKUP at ${requiredAirport}` };

    const campusDropoff = hasCampusStop(g, "DROPOFF", requiredCampus, campusByName);
    if (!campusDropoff) return { ok: false, reason: `No DROPOFF at ${requiredCampus}` };

    return { ok: true };
  }

  if (dir === "OUT") {
    const requiredAirport = t?.departing;
    const requiredCampus = t?.campus;
    if (!requiredAirport || !requiredCampus) return { ok: false, reason: "Missing airport/campus" };

    const campusPickup = hasCampusStop(g, "PICKUP", requiredCampus, campusByName);
    if (!campusPickup) return { ok: false, reason: `No PICKUP at ${requiredCampus}` };

    const airportDropoff = hasAirportStop(g, "DROPOFF", requiredAirport, airportNameByCode);
    if (!airportDropoff) return { ok: false, reason: `No DROPOFF at ${requiredAirport}` };

    return { ok: true };
}

  if (dir === "INTERNAL") {
    const from = t?.fromCampus;
    const to = t?.toCampus;

    if (!from || !to) return { ok: false, reason: "Missing from/to campus" };

    const fromPickup = hasCampusStop(g, "PICKUP", from, campusByName);
    const toDropoff = hasCampusStop(g, "DROPOFF", to, campusByName);

    if (!fromPickup) return { ok: false, reason: `No PICKUP at ${from}` };
    if (!toDropoff) return { ok: false, reason: `No DROPOFF at ${to}` };

    return { ok: true };
  }

  return { ok: false, reason: "Unknown journey" };
}

// utils/transportEligibility.js
export function splitEligiblePassengers(passengers, bus, ctx) {
  const eligible = [];
  const ineligible = []; // { passenger, reason }

  for (const p of passengers || []) {
    const res = busEligibilityForRow(p, bus, ctx);
    if (res.ok) eligible.push(p);
    else ineligible.push({ passenger: p, reason: res.reason || "Ineligible" });
  }

  return { eligible, ineligible };
}

export function summariseIneligible(ineligible) {
  if (!ineligible?.length) return "";
  const byReason = new Map();
  for (const x of ineligible) {
    const r = x.reason || "Ineligible";
    byReason.set(r, (byReason.get(r) || 0) + 1);
  }
  return Array.from(byReason.entries())
    .map(([reason, count]) => `${count} ${reason}`)
    .join(", ");
}